<template>
  <b-modal id="bv-modal-log-details" ref="detailsModal" hide-footer :hide-header-close="false"
    :no-close-on-backdrop="false" :no-close-on-esc="false" centered :size="fromHistory ? 'lg' : 'xl'"
    :modal-class="fromHistory ? 'details-modal-from-history' : ''"
    content-class="details-modal-wrapper" @hidden="onHidden">
    <template #modal-title>
      <div class="modal-title-text">{{ $t('resumodacopia') }}</div>
    </template>

    <div v-if="log || isMultiProcessMode" class="details-modal-content">
      
      <template v-if="fromHistory">
        <div class="origin-destination-card">
          <div class="origin-destination-row">
            <b-badge variant="success" class="origin-badge">{{ $t('origem') }}</b-badge>
            <span class="client-name">{{ clientOriginName }}</span>
            <b-icon icon="arrow-right" class="arrow-icon" />
            <b-badge variant="info" class="destination-badge">{{ $t('destino') }}</b-badge>
            <span class="client-name">{{ clientDestinationName }}</span>
          </div>
        </div>

        <div v-if="!isLogError" class="summary-section">
          <CopySummaryCollapses :created-count="resultsCreated" :substituted-count="resultsSubstituted"
            :ignored-count="resultsIgnored" :deleted-count="resultsDeleted" :created-items="createdItems"
            :substituted-items="substitutedItems" :ignored-items="ignoredItems" :deleted-items="deletedItems" />
        </div>

        <div v-if="isLogError" class="error-section">
          <b-alert show variant="danger" class="mb-0">
            <div class="d-flex align-items-center">
              <b-icon icon="exclamation-triangle-fill" class="mr-2" />
              <span>{{ $t('copiafalhou') }}</span>
            </div>
          </b-alert>
        </div>

        <div class="button-container">
          <b-button v-if="!isLogError" variant="primary" @click="downloadLogSummary()" style="margin-right: 15px;">
            {{ $t('baixarresumo') }}
          </b-button>
          <b-button variant="outline-secondary" @click="hide">{{ $t('fechar') }}</b-button>
        </div>
      </template>

      <template v-else>
      <div class="results-table-container">
        <DMPaginatedTable
          :fields="summaryTableFields"
          :data="summaryRows"
          :show-pagination="false"
          :show-action-buttons="false"
          :show-update-buttom="false"
          :show-delete-buttom="false"
        >
          <template #cell(scope)="data">
            <div class="scope-badges">
              <b-badge
                v-for="(scopeItem, index) in data.value"
                :key="`${data.item.id}-${index}`"
                variant="secondary"
                class="scope-badge"
              >
                {{ scopeItem }}
              </b-badge>
              <span v-if="!data.value || data.value.length === 0">-</span>
            </div>
          </template>

          <template #cell(status)="data">
            <b-badge
              :variant="getSummaryStatusVariant(data.value)"
              class="table-status-badge"
            >
              {{ getSummaryStatusLabel(data.value) }}
            </b-badge>
          </template>
        </DMPaginatedTable>
      </div>

      <div v-if="isMultiProcessMode && isProcessing" class="process-status-list">
        <div
          v-for="process in processingProcesses"
          :key="process.room"
          class="process-status-item"
        >
          <span class="process-module">{{ getSourceModuleLabel(process.sourceModule) }}</span>
          <span class="process-message">{{ getProcessMessage(process) }}</span>
          <b-progress
            :value="process.progress"
            variant="success"
            class="mt-2"
            height="1rem"
            animated
          />
        </div>
      </div>

      <div v-if="allCompleted && (!hasError || hasPartialSuccess)" class="summary-section">
        <CopySummaryCollapses :created-count="resultsCreated" :substituted-count="resultsSubstituted"
          :ignored-count="resultsIgnored" :deleted-count="resultsDeleted" :created-items="createdItems"
          :substituted-items="substitutedItems" :ignored-items="ignoredItems" :deleted-items="deletedItems" />
      </div>
      
      <div v-if="allCompleted && hasError && !hasPartialSuccess" class="error-section">
        <div class="error-summary-card">
          <div class="error-card-header">
            <div class="summary-header">
              <b-icon icon="info-circle" class="info-icon" />
              <span class="summary-title">{{ $t('resumodacopia') }}</span>
            </div>
            <p class="summary-description">{{ $t('visualizacaorapida') }}</p>
          </div>

          <div class="error-state-content">
            <div class="error-icon-wrapper">
              <b-icon icon="x-circle" class="error-icon-x-circle" />
            </div>
            <div class="error-state-title">{{ $t('importante') }}</div>
            <div class="error-state-message">
              {{ $t('copysummaryerrorstatus') }}
            </div>
          </div>
        </div>
      </div>

      <div v-if="allCompleted" class="button-container">
        <b-button v-if="!hasError || hasPartialSuccess" variant="primary" @click="downloadLogSummary()" style="margin-right: 15px;">
          {{ $t('baixarresumo') }}
        </b-button>
        <b-button :variant="hasError && !hasPartialSuccess ? 'primary' : 'outline-secondary'" @click="hide">
          {{ hasError && !hasPartialSuccess ? $t('ok') : $t('fechar') }}
        </b-button>
      </div>

      <CopyResultToastModal ref="copyResultToast" />
      </template>
    </div>
  </b-modal>
</template>

<script>
import CopySummaryCollapses from './CopySummaryCollapses.vue'
import CopyResultToastModal from './CopyResultToastModal.vue'
import DMPaginatedTable from '@/components/DMPaginatedTable'
import { hideLoader, showLoader } from '@/utils/loading'

export default {
  name: 'DetailsModal',

  components: {
    CopySummaryCollapses,
    CopyResultToastModal,
    DMPaginatedTable
  },

  props: {
    log: {
      type: Object,
      default: null
    },
    processes: {
      type: Array,
      default: () => []
    },
    clients: {
      type: Array,
      default: () => []
    },
    fromHistory: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    isMultiProcessMode() {
      return this.processes && this.processes.length > 0
    },

    resultsCreated() {
      if (this.isMultiProcessMode) {
        return this.processes.reduce((sum, p) => sum + (p.results?.created || 0), 0)
      }
      return (this.log && this.log.results && this.log.results.created) || 0
    },

    resultsSubstituted() {
      if (this.isMultiProcessMode) {
        return this.processes.reduce((sum, p) => sum + (p.results?.substituted || 0), 0)
      }
      return (this.log && this.log.results && this.log.results.substituted) || 0
    },

    resultsIgnored() {
      if (this.isMultiProcessMode) {
        return this.processes.reduce((sum, p) => sum + (p.results?.ignored || 0), 0)
      }
      return (this.log && this.log.results && this.log.results.ignored) || 0
    },

    resultsDeleted() {
      if (this.isMultiProcessMode) {
        return this.processes.reduce((sum, p) => sum + (p.results?.deleted || 0), 0)
      }
      return (this.log && this.log.results && this.log.results.deleted) || 0
    },

    allDetails() {
      if (this.isMultiProcessMode) {
        return this.processes.flatMap(p => p.results?.details || [])
      }
      if (!this.log || !this.log.results || !this.log.results.details) return []
      return this.log.results.details
    },

    createdItems() {
      return this.allDetails.filter((d) => d.action === 'created')
    },

    substitutedItems() {
      return this.allDetails.filter((d) => d.action === 'substituted')
    },

    ignoredItems() {
      return this.allDetails.filter((d) => d.action === 'ignored')
    },

    deletedItems() {
      return this.allDetails.filter((d) => d.action === 'deleted')
    },

    clientOriginName() {
      if (this.isMultiProcessMode && this.processes.length > 0) {
        const client = this.clients.find(c => c.code === this.fromClient)
        return client ? client.name : '-'
      }
      if (!this.log || !this.log.clientOrigin) return '-'
      return this.log.clientOrigin.name || '-'
    },

    clientDestinationName() {
      if (this.isMultiProcessMode && this.processes.length > 0) {
        const client = this.clients.find(c => c.code === this.toClient)
        return client ? client.name : '-'
      }
      if (!this.log || !this.log.clientDestination) return '-'
      return this.log.clientDestination.name || '-'
    },

    fromClient() {
      return this.$store.state.copyProcesses?.fromClient || null
    },

    toClient() {
      return this.$store.state.copyProcesses?.toClient || null
    },

    isProcessing() {
      if (!this.isMultiProcessMode) return false
      return this.processes.some(p => p.status === 'processing')
    },

    allCompleted() {
      if (!this.isMultiProcessMode) return true
      return this.processes.every(p => p.status === 'success' || p.status === 'error')
    },

    hasError() {
      if (!this.isMultiProcessMode) return false
      return this.processes.some(p => p.status === 'error')
    },

    hasPartialSuccess() {
      if (!this.isMultiProcessMode || this.processes.length < 2) return false
      const hasSuccess = this.processes.some(p => p.status === 'success')
      const hasAnyError = this.processes.some(p => p.status === 'error')
      return hasSuccess && hasAnyError
    },

    isLogError() {
      if (!this.log || !this.fromHistory) return false
      const s = this.log.status
      return s === 'error' || s === 'failure'
    },

    summaryTableFields() {
      return [
        {
          key: 'origin',
          label: this.$t('origem'),
          class: 'text-center'
        },
        {
          key: 'destination',
          label: this.$t('destino'),
          class: 'text-center'
        },
        {
          key: 'copiedFrom',
          label: this.$t('copiadode'),
          class: 'text-center'
        },
        {
          key: 'scope',
          label: this.$t('escopo'),
          class: 'text-center'
        },
        {
          key: 'status',
          label: this.$t('status'),
          class: 'text-center'
        }
      ]
    },

    summaryRows() {
      if (this.isMultiProcessMode) {
        return this.processes.map((process) => ({
          id: process.room,
          origin: this.clientOriginName,
          destination: this.clientDestinationName,
          copiedFrom: this.getSourceModuleLabel(process.sourceModule),
          scope: this.extractScopeItems(process.scope),
          status: process.status
        }))
      }

      if (!this.log) return []

      const sourceModule = Array.isArray(this.log.sourceModule)
        ? this.log.sourceModule[0]
        : this.log.sourceModule

      return [
        {
          id: this.log._id || 'single-log',
          origin: this.clientOriginName,
          destination: this.clientDestinationName,
          copiedFrom: sourceModule ? this.getSourceModuleLabel(sourceModule) : '-',
          scope: this.extractScopeItems(this.log.scope),
          status: this.log.status || 'success'
        }
      ]
    },

    processingProcesses() {
      if (!this.isMultiProcessMode) return []
      return this.processes.filter((process) => process.status === 'processing')
    }
  },

  watch: {
    allCompleted: {
      handler(newVal, oldVal) {
        if (!this.fromHistory && this.isMultiProcessMode && newVal && !oldVal) {
          this.$nextTick(() => {
            const type = this.hasPartialSuccess ? 'warning' : (this.hasError ? 'error' : 'success')
            this.$refs.copyResultToast?.show(type)
          })
        }
      }
    }
  },

  methods: {
    show() {
      this.$refs.detailsModal.show()
    },

    hide() {
      this.$refs.detailsModal.hide()
    },

    getStatusLabel(status) {
      const labels = {
        processing: this.$t('emexecucao') + ' - ' + this.$t('aguardeconclusao'),
        success: this.$t('sucesso'),
        error: this.$t('erro')
      }
      return labels[status] || status
    },

    getStatusVariant(status) {
      const variants = {
        processing: 'warning',
        success: 'success',
        error: 'danger'
      }
      return variants[status] || 'secondary'
    },

    getSourceModuleLabel(sourceModule) {
      const labels = {
        configuration: this.$t('deondedesejacopiarconfiguracao'),
        recruitment: this.$t('deondedesejacopiarres')
      }
      return labels[sourceModule] || sourceModule
    },

    getScopeLabel(scopeKey) {
      const labels = {
        activities: this.$t('atividades'),
        profile: this.$t('perfil'),
        experience: this.$t('experiencia'),
        skills: this.$t('habilidades'),
        positionRole: this.$t('vinculocargofuncao')
      }
      return labels[scopeKey] || scopeKey
    },

    extractScopeItems(scope) {
      if (!scope) return []
      if (Array.isArray(scope)) {
        return scope.map((scopeItem) => this.getScopeLabel(scopeItem))
      }
      return Object.keys(scope)
        .filter((key) => scope[key])
        .map((key) => this.getScopeLabel(key))
    },

    getSummaryStatusLabel(status) {
      const labels = {
        pending: this.$t('pendente'),
        processing: this.$t('emprocessamento'),
        success: this.$t('sucesso'),
        failure: this.$t('falha'),
        denied: this.$t('negado'),
        error: this.$t('erro')
      }
      return labels[status] || status
    },

    getSummaryStatusVariant(status) {
      const variants = {
        pending: 'secondary',
        processing: 'warning',
        success: 'success',
        failure: 'danger',
        denied: 'warning',
        error: 'danger'
      }
      return variants[status] || 'secondary'
    },

    getProcessMessage(process) {
      if (process.messageKey) {
        return this.$t(process.messageKey, process.messageParams || {})
      }
      return process.message || ''
    },

    onHidden() {
      this.$emit('hidden')
      if (this.isMultiProcessMode) {
        this.$emit('closed')
      }
    },
    async downloadLogSummary() {
      try {
        showLoader()
        const { exportCopyLog } = await import('@/services/admin-service')

        if (this.isMultiProcessMode) {
          for (const process of this.processes) {
            if (process.logId) {
              const { data: blob } = await exportCopyLog(process.logId)
              this.downloadLog(blob, process.logId)
            }
          }
        } else {
          if (this.log && this.log._id) {
            const { data: blob } = await exportCopyLog(this.log._id)
            this.downloadLog(blob, this.log._id)
          } else {
            throw new Error(this.$t('copysummarylogidnaoencontrado'))
          }
        }
      } catch (error) {
        console.error('Error downloading log:', error)
        this.$emit('alert', {
          message: this.$t('erroaobaixarresumo'),
          type: 'danger'
        })
      } finally {
        hideLoader()
      }
    },

    downloadLog(data) {
      const url = window.URL.createObjectURL(new Blob([data], { type: '' }))
      const link = document.createElement('a')
      link.href = url
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-')
      const fileName = `resumo-copia-${timestamp}.xlsx`
      link.setAttribute('download', fileName)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    },
  }
}
</script>

<style scoped>
.modal-title-text {
  color: #5a9e6f;
  font-weight: 400;
  font-size: 1.25rem;
}

.details-modal-content {
  padding: 0;
  width: 100%;
}

.origin-destination-card {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 15px 20px;
  margin-bottom: 15px;
  width: 100%;
}

.origin-destination-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
}

.origin-badge {
  background-color: #5a9e6f !important;
  font-weight: 500;
  padding: 5px 12px;
  border-radius: 15px;
}

.destination-badge {
  background-color: #6c9ec4 !important;
  font-weight: 500;
  padding: 5px 12px;
  border-radius: 15px;
}

.client-name {
  font-weight: 500;
  color: #333;
}

.arrow-icon {
  color: #5a9e6f;
  margin: 0 5px;
}

.results-table-container {
  margin-bottom: 12px;
  width: 100%;
}

.scope-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  justify-content: center;
}

.scope-badge {
  background-color: #717171 !important;
  font-weight: 500;
  font-size: 0.75rem;
}

.table-status-badge {
  font-weight: 500;
  font-size: 0.82rem;
}

.process-status-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
}

.process-status-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px 16px;
}

.process-info {
  display: flex;
  flex-direction: column;
  margin-top: 10px;
  gap: 5px;
}

.process-module {
  font-weight: 600;
  color: #5a9e6f;
  font-size: 0.9rem;
}

.process-message {
  color: #666;
  font-size: 0.85rem;
  font-style: italic;
}

.summary-section {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  margin-top: 0;
  margin-bottom: 16px;
  width: 100%;
}

.error-section {
  margin-bottom: 16px;
  width: 100%;
}

.error-summary-card {
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 14px 16px 18px;
}

.error-card-header {
  text-align: left;
  margin-bottom: 14px;
}

.summary-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.info-icon {
  color: #1f8fff;
  font-size: 1rem;
}

.summary-title {
  font-weight: 600;
  font-size: 1.1rem;
  color: #2d2d2d;
}

.summary-description {
  color: #666;
  font-size: 0.8rem;
  margin: 0;
  padding-left: 1.5rem;
  line-height: 1.35;
}

.error-state-content {
  text-align: center;
  padding: 10px 6px 2px;
}

.error-icon-wrapper {
  margin: 0 auto 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.error-icon-x-circle {
  color: #E67774;
  font-size: 5rem;
}

.error-state-title {
  font-weight: 700;
  font-size: 1.25rem;
  color: #111;
  margin-bottom: 8px;
}

.error-state-message {
  color: #222;
  font-size: 0.95rem;
}

.button-container {
  display: flex;
  justify-content: center;
  margin-top: 10px;
  width: 100%;
}
</style>

<style>
.details-modal-wrapper .modal-body {
  width: 100%;
  padding: 16px 20px 20px;
}

.details-modal-wrapper .modal-title {
  width: 100%;
  text-align: left;
}

.details-modal-from-history .modal-title {
  text-align: center;
}

.details-modal-wrapper .modal-dialog {
  max-width: 960px;
}

.details-modal-wrapper .mt-3 {
  margin-top: 0 !important;
}
</style>
